<template lang="pug">
uc-preload-sfx(name="drag-yes")
uc-preload-sfx(name="drag-no")
.drag_n_drop.relative.z-0.w-full(:id="screen.id" ref="el")
  template(v-if="!wordDoc && !audioDoc")
    .dots.text-center.mb-4.md_hidden.h-2
      .w-2.h-2.m-1.bg-gray-300.rounded-full.inline-block(
        :data-drag-item-dot="item.title.trim()"
        :key="item.title"
        uc-word-skip=""
        v-for="item in items"
      )

    //- Handlers live on the container, not on .drag-item, so event delegation
    //- survives reset() — cloneNode(true) copies DOM attributes but NOT Vue listeners.
    .drag-items.fade-in(
      @click="onDragAreaClick"
      @keydown.enter.prevent="onDragAreaKey"
      @keydown.space.prevent="onDragAreaKey"
    )
      .drag-item.first_inline-block.hidden.md_inline-block.rounded.formal_rounded-sm.m-1(
        :data-drag-item-home="item.title"
        :data-flip-id="item.title.trim()"
        :key="item.title"
        class="md_max-w-[12rem] md_[text-wrap:balance]"
        v-for="item in items"
        tabindex="0"
        role="button"
        :aria-label="`Drag or click to place: ${item.title}`"
        aria-haspopup="dialog"
      )
        .inline-block(
          :data-drag-item="item.title"
        )
          .inline-block.flex.flex-col.gap-2.items-center.justify-center(
            :class="twMerge(item.img ? 'bg-paper shadow-sm' : 'bg-gray-200 dark_bg-gray-900 border-primary-500/20 dark_border-primary-500/20', 'p-4 formal_rounded-sm rounded shadow-sm', 'md:cursor-grab max-md:cursor-pointer max-md:active:scale-95 max-md:transition-transform', item.style)"
            :data-drag-item-content="item.id"
          )
            uc-asset(
              :style="[{ height: item.imgSize ? `${item.imgSize}rem` : '5rem', width: item.imgSize ? `${item.imgSize}rem` : 'auto' }, { 'object-fit': item.imgSize ? 'contain' : 'cover' }]"
              :src="item.img"
              v-if="item.img"
            )
            uc-text.dark_text-white(
              :k="`${item.catId}.items.${item.index}`"
              :text="item.title"
              :data-drag-item-label="item.title"
            )

    //- Bottom-sheet category picker.
    //- Teleported to <body> to escape any stacking-context limits on the parent.
    //- Opens only when a drag item is explicitly clicked/activated (Enter/Space).
    Teleport(to="body")
      .fixed.inset-0(
        v-if="activeMobileItem"
        :class="[courseCssClasses, { dark: isDark }]"
        style="z-index:9999"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="`mpick-${screen.id}`"
        @keydown.esc="closeMobileSelect"
      )
        .absolute.inset-0(
          aria-hidden="true"
          :style="isMobile ? 'background:rgba(0,0,0,0.45)' : 'background:transparent'"
          @click="closeMobileSelect"
        )
        .absolute.bg-paper.corners.surface.shadow-xl(
          ref="modalPanel"
          :class="isMobile ? 'bottom-0 left-0 right-0 rounded-t-2xl' : 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-xl w-80'"
          style="padding:1.5rem;max-height:80vh;overflow-y:auto"
          @keydown.tab="trapFocus"
        )
          .flex.justify-between.items-center.gap-4.mb-5
            //- text="Move ${title} to:"
            uc-text.heading.text-sm.md_text-base.m-0(
              text="Move ${title} to:"
              k="Move ${title} to:"
              type="h2"
              :values="{ title: activeMobileItem.title }"
            )
            uc-button.btn-icon.ripple.ripple--centered.shrink-0(
              @click="closeMobileSelect"
              :aria-label="hasTranslation('Cancel', 'Cancel')"
            ) ✕
          .flex.flex-col.gap-3
            uc-button.btn.btn-primary.w-full.ripple(
              v-for="cat in categories"
              :key="cat.title"
              :k="`${cat?.id}.title`"
              :text="cat.title"
              @click="onMobileCategorySelect(cat)"
            )
            uc-button.btn.w-full.ripple(
              text="Cancel"
              @click="closeMobileSelect"
            )

    .drop-items.-z-1.flex.flex-col.md_grid.gap-5.items-stretch.relative.my-6.w-full.max-w-screen-lg.mx-auto.slide-up(
      :style="{ gridTemplateColumns: categories.length < 5 ? `repeat(${categories.length}, minmax(0, 1fr))` : `repeat(auto-fit, minmax(200px, 1fr))` }"
      uc-word-skip=""
    )
      .drop-item.h-full.transition.rounded.formal_rounded-sm.ring-0(
        :data-drag-target="cat.title"
        class="ring-primary-500/20"
        v-for="cat in categories"
      )
        .drag-targets.flex.flex-col.h-full.rounded.formal_rounded-sm.overflow-hidden.bg-white.dark_bg-gray-900.shadow-sm
          .group-header
            uc-editable(
              :class="twMerge('heading inline-block border-none my-0 text-black dark_text-white bg-secondary-500 dark_bg-secondary-900 w-full p-2 text-sm md_text-lg', cat?.styles?.title || '')"
              :editPath="`${editPath}.categories.${cat.index}.title`"
              :k="`${cat?.id}.title`"
              :text="cat.title"
              type="span"
              v-if="cat.title"
            )
          .group-container.flex-1.flex.items-center.justify-center.p-2.bg-cover.bg-no-repeat.bg-center(
            :style="[cat.bgImg ? { backgroundImage: setBgImgURLs(cat.bgImg) } : {}]"
            :class="twMerge(dropContainerClass, cat?.styles?.content || '')"
            :data-drag-target-container="cat.title"
          )
  
  //- in the word doc we will list the items under their correct category
  template(
    v-if="wordDoc && !audioDoc"
  )
    .cat(
      v-for="cat in categories"
    )
      uc-text.font-bold(
        :k="`${cat?.id}.title`"
        :text="cat.title"
        type="p"
      )
      ul.items
        template(
          v-for="item in items"
        )
          li(
            v-if="item.id === cat.index"
          )
            uc-text(
              :k="`${item.catId}.items.${item.index}`"
              :text="item.title"
              type="p"
            )

  div.fade-in(v-if="screen.feedback")
    uc-text.font-bold(text="Feedback" type="p" v-if="wordDoc && !audioDoc")
    audio-marker(:name="`${screen.id}-feedback`")
    transition-expand 
      .flex.items-center.py-6(v-if="suspend.completed[screen.id] || audioDoc || wordDoc")
        uc-editable(
          :editPath="`${editPath}.feedback`"
          :k="`${screen.id}.feedback`"
          :text="screen.feedback"
        )
</template>

<script setup lang="ts">
import {
  nextTick,
  onMounted,
  ref,
  unref,
  inject,
  watch,
  computed,
  toRef
} from 'vue';
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
import { twMerge } from '../lib/mergeStyles';
import Draggable from 'gsap/Draggable';
import gsap from 'gsap';
import shuffle from '../lib/shuffle';
import Flip from 'gsap/Flip';
import $ from 'jquery';
import { setCompleted } from '../lib/SCORM';
import useSuspend from '../lib/useSuspend';
import { playSfx } from '../lib/sfx';
import isQuizPage from '../lib/isQuizPage';
import useScreenAudio from '../lib/useScreenAudio';
import dragSchema from '../lib/screen-schemas/screen-drag.schema';
import expandUploadPath from '../lib/expandUploadPath';
import useTranslations from '../stores/useTranslations';

const suspend = useSuspend();
const audioDoc = inject('audioDoc') as boolean;
const wordDoc = unref<boolean>(inject('wordDoc'));
const hasSfx = inject('hasSfx');
const pageId = inject('pageId') as object;
const { translations } = useTranslations();

// Bring the active theme + colour-palette classes into the Teleport so that
// *:where(.theme-*) CSS selectors match inside the bottom-sheet dialog.
const courseCssClasses = inject<Ref<string[]>>('courseCssClasses', ref([]));

// Mirror the nearest .dark ancestor so the sheet matches the screen's dark mode.
const isDark = computed(() => !!el.value?.closest('.dark'));

// VueUse breakpoints
const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobile = breakpoints.smallerOrEqual('md');

const setBgImgURLs = (path: string) => {
  const paths = path.trim().split(' ').map(expandUploadPath);
  return paths.length === 1
    ? `url(${paths[0]})`
    : paths.map(p => `url(${p})`).join(', ');
};

gsap.registerPlugin(Flip);
gsap.registerPlugin(Draggable);

const props = defineProps<{
  editPath: string;
  screen: UcScreen & {
    categories: UcScreen[] & { items: string[] }[];
  };
}>();

const { playSlot } = useScreenAudio(toRef(props, 'screen'), dragSchema);

const shuffled = computed(() =>
  props.screen['shuffle-items'] === false ? false : true
);

const el = ref<HTMLElement | null>(null);

const categories = computed(() =>
  props.screen.categories.map((c, index) => ({
    ...c,
    index,
    title: c.title.trim(),
    bgImg: c?.bg
  }))
);

const items = computed(() => {
  let result: any[] = [];

  for (let c of categories.value) {
    c.items.forEach((item: string, index) => {
      let itemData = {
        id: c.index,
        catId: c.id,
        index,
        title: item?.title?.trim() || item?.trim(),
        img: item?.img,
        imgSize: item['img-size'] || 8,
        style: item?.style ?? ''
      };

      result.push(itemData);
    });
  }

  result.sort(function (a, b) {
    let valueA: unknown, valueB: unknown;

    valueA = a['title'];
    valueB = b['title'];
    if (valueA < valueB) {
      return -1;
    } else if (valueA > valueB) {
      return 1;
    }
    return 0;
  });

  return shuffled.value ? shuffle(result) : result;
});

// The item currently being placed via the mobile picker.
const activeMobileItem = ref<(typeof items.value)[number] | null>(null);

// Ref to the bottom-sheet panel element for focus-trap logic.
const modalPanel = ref<HTMLElement | null>(null);

// Element to return focus to when the picker closes.
const mobileSelectFocusReturn = ref<HTMLElement | null>(null);

/** Delegated click handler on .drag-items — survives cloneNode resets. */
function onDragAreaClick(event: MouseEvent) {
  const dragItemEl = (event.target as HTMLElement).closest<HTMLElement>(
    '[data-drag-item-home]'
  );
  if (!dragItemEl) return;
  const title = dragItemEl.getAttribute('data-drag-item-home')?.trim();
  const item = items.value.find(i => i.title.trim() === title);
  if (item) openMobileSelect(item, dragItemEl);
}

/** Delegated Enter/Space handler on .drag-items — survives cloneNode resets. */
function onDragAreaKey(event: KeyboardEvent) {
  const dragItemEl = (event.target as HTMLElement).closest<HTMLElement>(
    '[data-drag-item-home]'
  );
  if (!dragItemEl) return;
  const title = dragItemEl.getAttribute('data-drag-item-home')?.trim();
  const item = items.value.find(i => i.title.trim() === title);
  if (item) openMobileSelect(item, dragItemEl);
}

/** Open the mobile category picker for the given item. */
function openMobileSelect(
  item: (typeof items.value)[number],
  triggerEl: HTMLElement
) {
  activeMobileItem.value = item;
  mobileSelectFocusReturn.value = triggerEl;
  // Lock scroll on both html and body (covers apps that scroll either element).
  document.documentElement.style.overflow = 'hidden';
  document.body.style.overflow = 'hidden';
  // Mark the app shell as inert so keyboard users and screen readers cannot
  // reach background content while the dialog is open.
  document.querySelector('#app')?.setAttribute('inert', '');
  nextTick(() => {
    const firstBtn = modalPanel.value?.querySelector<HTMLElement>('button');
    firstBtn?.focus();
  });
}

/** Close the picker and restore scroll + focus to the triggering element. */
function closeMobileSelect() {
  const returnTarget = mobileSelectFocusReturn.value;
  activeMobileItem.value = null;
  mobileSelectFocusReturn.value = null;
  document.documentElement.style.overflow = '';
  document.body.style.overflow = '';
  document.querySelector('#app')?.removeAttribute('inert');
  nextTick(() => returnTarget?.focus());
}

/**
 * Tab-key focus trap for the bottom-sheet panel.
 * Cycles focus through the panel's buttons without leaving the dialog.
 */
function trapFocus(event: KeyboardEvent) {
  const panel = modalPanel.value;
  if (!panel) return;
  const btns = [...panel.querySelectorAll<HTMLElement>('button')];
  const first = btns[0];
  const last = btns[btns.length - 1];
  if (event.shiftKey) {
    if (document.activeElement === first) {
      event.preventDefault();
      last.focus();
    }
  } else {
    if (document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

/**
 * Imperatively sync tabindex/role/aria-* on every .drag-item currently in the
 * DOM. Applied unconditionally on both mobile and desktop so that:
 *  - Keyboard users can Tab to items and activate the picker on any viewport.
 *  - reset() rebuilds items via cloneNode(true), which copies attributes from
 *    the mount-time DOM — this re-applies correct values after each reset.
 *  - GSAP-mutated DOM is not watched by Vue's reactivity system, so bindings
 *    on removed/re-inserted elements are not live.
 */
function syncDragItemAttrs() {
  if (!el.value) return;
  el.value
    .querySelectorAll<HTMLElement>('.drag-items .drag-item')
    .forEach(item => {
      item.setAttribute('tabindex', '0');
      item.setAttribute('role', 'button');
      const title = item.getAttribute('data-drag-item-home') || '';
      item.setAttribute('aria-label', `Drag or click to place: ${title}`);
      item.setAttribute('aria-haspopup', 'dialog');
    });
}

/** Called when the user picks a category from the bottom-sheet. */
function onMobileCategorySelect(cat: (typeof categories.value)[number]) {
  const item = activeMobileItem.value;
  if (!item) return;
  // Close first so the shake/success animation plays against a clean UI.
  closeMobileSelect();
  dropViaDialog(item, cat);
}

const hasDragImages = computed(() => {
  return items.value.some(item => item.img);
});

const hasDropImages = computed(() => {
  return categories.value.some(cat => cat.bgImg);
});

const dropContainerClass = computed(() => {
  const classes = [];
  if (hasDragImages.value) {
    classes.push('flex-row md_flex-col gap-8 min-h-[8rem] md_min-h-[10rem]');
  } else {
    classes.push('flex-col gap-2 min-h-[6rem]');
    classes.push(
      items.value.length > 4 ? 'md_min-h-[10rem]' : 'md_min-h-[8rem]'
    );
  }
  return classes.join(' ');
});

const hasTranslation = (key: string, fallback: string) => {
  // get the translation if it exists
  let maybeTranslated =
    translations?.[suspend.locale]?.[key] ||
    translations?.[suspend.locale]?.[fallback];

  return maybeTranslated || key;
};

function dropViaDialog(item: { title: string }, category: { title: string }) {
  const itemName = item.title.trim();
  const catItems = categories.value
    ?.find(obj => obj.title === category.title)
    ?.items.map(s => s?.title?.trim() || s?.trim());

  const dragElement = el.value?.querySelector(
    `[data-drag-item="${item.title}"]`
  );
  const targetElement = el.value?.querySelector(
    `[data-drag-target="${category.title}"]`
  );

  if (!catItems?.includes(itemName)) {
    // Wrong category — shake the target and play the error sound.
    if (targetElement) {
      gsap
        .timeline()
        .to(targetElement, {
          x: '+=50',
          transitionDuration: 0,
          duration: 0.1
        })
        .to(targetElement, {
          x: 0,
          ease: 'elastic.out(1.5, 0.2)',
          duration: 1,
          clearProps: 'transitionDuration'
        });
    }

    if (hasSfx.value) playSfx('drag-no');
  } else {
    // Correct category — fade the item out and move it to the target.
    if (dragElement && targetElement) {
      gsap.to(dragElement.closest('[data-drag-item-home]'), {
        opacity: 0,
        scale: 0.8,
        duration: 0.3,
        onComplete: () => {
          const dragObject = items.value.find(
            i => i.title.trim() === item.title.trim()
          );
          const hasImg = dragElement.querySelector('img');
          const dropElement =
            hasImg || (dragObject?.style?.length ?? 0) > 0
              ? dragElement.querySelector('[data-drag-item-content]')
              : dragElement.querySelector('[data-drag-item-label]');

          const targetContainer = targetElement.querySelector(
            '[data-drag-target-container]'
          );

          if (dropElement && targetContainer) {
            targetContainer.appendChild(dropElement);
            (dropElement as HTMLElement).dataset.dropped = '';

            dragElement.closest('[data-drag-item-home]')?.remove();
            el.value?.querySelector('[data-drag-item-dot]')?.remove();
            // The next .drag-item is now first — ensure it has correct a11y attrs.
            syncDragItemAttrs();

            if ((hasSfx as any)?.value) playSfx('drag-yes');

            if (!el.value?.querySelector('[data-drag-item-dot]')) {
              const section = el.value?.closest('.section');
              if (section) setCompleted(section.id);
              setCompleted(props.screen.id);

              playSlot('feedback');
            }
          }
        }
      });
    }
  }
}

onMounted(() => {
  const dragTargets = el?.value.querySelectorAll('[data-drag-target]');
  let draggableInstances: Draggable[] = [];

  // Store the original order and elements when component first loads
  const originalItemOrder: string[] = [];
  const originalElements = new Map();
  const originalDotElements = new Map();

  const initialDragItems = el.value.querySelectorAll('.drag-items .drag-item');
  initialDragItems.forEach(dragItem => {
    const itemTitle = dragItem.getAttribute('data-drag-item-home');
    if (itemTitle) {
      originalItemOrder.push(itemTitle);
      // Store a clone of the original element for later use
      originalElements.set(itemTitle, dragItem.cloneNode(true));
    }
  });

  // Store original dot elements
  const initialDots = el.value.querySelectorAll('.dots [data-drag-item-dot]');
  initialDots.forEach(dot => {
    const itemTitle = dot.getAttribute('data-drag-item-dot');
    if (itemTitle) {
      originalDotElements.set(itemTitle, dot.cloneNode(true));
    }
  });

  const createDraggables = () => {
    // Kill existing instances first
    draggableInstances.forEach(draggable => draggable.kill());
    draggableInstances = [];

    if (isMobile.value) {
      return [];
    }

    return Draggable.create(el?.value.querySelectorAll('[data-drag-item]'), {
      type: 'x,y',

      onDrag() {
        let hit;

        dragTargets.forEach(t => {
          if (this.hitTest(t, '50%')) {
            hit = true;
            $(dragTargets).removeClass('ring-8');
            $(t).addClass('ring-8');
          }
        });

        if (!hit) {
          $(dragTargets).removeClass('ring-8');
        }
      },

      async onDragEnd() {
        $(dragTargets).removeClass('ring-8');

        await Promise.all(
          [...dragTargets].map(async t => {
            if (this.hitTest(t, '50%')) {
              const itemEls = $(
                '.drag-items .drag-item, [data-drag-item-dot]',
                el.value
              ).get();

              const droppedEls = $('[data-dropped]', el.value).get();
              const itemName = this.target.dataset.dragItem.trim();
              const catName = (t as HTMLElement).dataset.dragTarget;

              const catItems = Object.values(categories.value)
                ?.find(obj => obj.title === catName)
                ?.items.map(s => s?.title?.trim() || s?.trim());

              const dragObject = Object.values(items.value).find(
                item => item.title.trim() === itemName
              );

              if (!catItems.includes(itemName) && this.hitTest(t)) {
                // nope
                gsap.to(this.target, { x: 0, y: 0 });
                gsap
                  .timeline()
                  .to(t, {
                    x: '+=50',
                    transitionDuration: 0,
                    duration: 0.1,
                    onComplete: () => {
                      if (hasSfx.value) playSfx('drag-no');
                    }
                  })
                  .to(t, {
                    x: 0,
                    ease: 'elastic.out(1.5, 0.2)',
                    duration: 1,
                    clearProps: 'transitionDuration'
                  });
              } else {
                let dropElement = $('[data-drag-item-label]', this.target)[0];
                // if the drag item has an image, or has yaml styles applied, add the drag item content (includes the container element with styles etc.) otherwise just add the label
                if (
                  this.target.querySelector('img') ||
                  dragObject?.style?.length > 0
                ) {
                  dropElement = $('[data-drag-item-content]', this.target)[0];
                }
                const itemHome = this.target.closest('[data-drag-item-home]');
                const dragTargetContainer = $(
                  '[data-drag-target-container]',
                  t
                );

                if (itemHome) itemHome.style.width = this.target.offsetWidth;

                let flipId = Date.now().toString();
                let batch = Flip.batch(flipId);

                batch.add({
                  getState() {
                    return Flip.getState(dropElement);
                  },
                  animate(self) {
                    return Flip.from(self.state, {
                      duration: 1
                    });
                  }
                });

                batch.add({
                  getState() {
                    return Flip.getState(itemEls);
                  },
                  animate(self) {
                    return Flip.from(self.state, {
                      duration: 0.5,
                      ease: 'power1.inOut',
                      stagger: 0.005,
                      onLeave: elements =>
                        gsap.to(elements, {
                          opacity: 0.5,
                          scaleX: 0,
                          duration: 0.2,
                          onComplete: () => {
                            if (hasSfx.value) playSfx('drag-yes');
                          }
                        })
                    });
                  }
                });

                batch.add({
                  getState() {
                    return Flip.getState(droppedEls);
                  },
                  animate(self) {
                    return Flip.from(self.state, {
                      duration: 0.25
                    });
                  }
                });

                batch.add({
                  getState() {
                    return Flip.getState(dragTargetContainer);
                  },
                  animate(self) {
                    return Flip.from(self.state, {
                      duration: 0.2
                    });
                  }
                });

                batch.getState();

                dragTargetContainer.append(dropElement);
                if (itemHome) $(itemHome).remove();
                $(`[data-drag-item-dot]:first-child`, el.value)?.remove();
                (dropElement as HTMLElement).dataset.dropped = '';

                batch.run(true);

                if ($(`[data-drag-item-dot]`, el.value).length === 0) {
                  const section = el.value?.closest('.section');
                  if (section) setCompleted(section.id);
                  setCompleted(props.screen.id);

                  playSlot('feedback');
                }
              }
            } else {
              gsap.to(this.target, { x: 0, y: 0 });
            }
          })
        );

        this.update(true, true);
      }
    });
  };

  // Initial creation of draggables (skipped on mobile — bottom-sheet picker only)
  draggableInstances = createDraggables();
  // Ensure attrs match whichever viewport is active at mount.
  syncDragItemAttrs();

  // Re-sync whenever the breakpoint crosses the mobile/desktop boundary.
  watch(isMobile, () => {
    // Close the picker if the user resizes to desktop while it is open.
    if (!isMobile.value && activeMobileItem.value) {
      closeMobileSelect();
    }
    nextTick(() => {
      syncDragItemAttrs();
      draggableInstances = createDraggables();
    });
  });

  const reset = () => {
    // Close the picker if open
    activeMobileItem.value = null;
    document.body.style.overflow = '';

    // Get all dropped items
    const droppedItems = el.value.querySelectorAll('[data-dropped]');
    const dragItemsContainer = el.value.querySelector('.drag-items');

    // Move all dropped items back to their original positions
    // Create a map of dropped items to their original positions
    const droppedItemsMap = new Map();
    droppedItems.forEach(droppedItem => {
      const itemTitle = droppedItem.textContent?.trim();
      if (itemTitle) {
        droppedItemsMap.set(itemTitle, droppedItem);
      }
    });

    // Add dropped items back in their original positions using the stored order
    originalItemOrder.forEach(itemTitle => {
      if (droppedItemsMap.has(itemTitle)) {
        const droppedItem = droppedItemsMap.get(itemTitle);

        // Clone the stored original element
        const originalElement = originalElements.get(itemTitle);
        if (originalElement && dragItemsContainer) {
          const clonedElement = originalElement.cloneNode(true);
          dragItemsContainer.appendChild(clonedElement);

          // Remove the dropped item
          droppedItem.remove();
        }
      }
    });

    // Recreate dots for mobile view using stored elements
    const dotsContainer = el.value.querySelector('.dots');
    if (dotsContainer) {
      dotsContainer.innerHTML = '';
      originalItemOrder.forEach(itemTitle => {
        const originalDot = originalDotElements.get(itemTitle);
        if (originalDot) {
          const clonedDot = originalDot.cloneNode(true);
          dotsContainer.appendChild(clonedDot);
        }
      });
    }

    draggableInstances = createDraggables();
    // Clones may have been taken on a different viewport — re-apply correct attrs.
    syncDragItemAttrs();
  };

  // listen for the quizReset event
  const app = document.querySelector('#app');
  app?.addEventListener('quizReset', async e => {
    if (await isQuizPage(e, pageId)) reset();
  });

  // the KS has a reset screen button, so listen for the resetting of the screen's completion
  watch(
    () => (props.screen.id ? suspend.completed[props.screen.id] : false),
    (newValue, oldValue) => {
      // Only reset if completion changed from true to false
      if (oldValue && !newValue) {
        reset();
      }
    },
    { flush: 'post' }
  );
});
</script>

<style scoped>
@media (min-width: 768px) {
  .drag-items {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    align-content: center;
  }
}

/* Visible focus ring for drag items when keyboard-navigated on mobile. */
.drag-item:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
}
:global(.dark) .drag-item:focus-visible {
  outline-color: #93c5fd;
}
</style>
